/**
 * Sidebar Context
 *
 * Purpose: Provides global state management for sidebar open/collapsed state
 *          that persists across page navigations
 *
 * Architecture: React Context with localStorage persistence
 *
 * Dependencies: React
 * Author: Development Team
 * Created: 2025-11-29
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SidebarContextType {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const STORAGE_KEY = 'orchestrate_sidebar_open';

interface SidebarProviderProps {
  children: ReactNode;
}

/**
 * SidebarProvider Component
 *
 * Purpose: Provides sidebar state to all child components
 * Logic: Uses localStorage to persist state across sessions
 */
export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  // Initialize from localStorage, default to collapsed (false)
  const [sidebarOpen, setSidebarOpenState] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  // Persist to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(sidebarOpen));
  }, [sidebarOpen]);

  const setSidebarOpen = (open: boolean) => {
    setSidebarOpenState(open);
  };

  const toggleSidebar = () => {
    setSidebarOpenState((prev) => !prev);
  };

  return (
    <SidebarContext.Provider value={{ sidebarOpen, setSidebarOpen, toggleSidebar }}>
      {children}
    </SidebarContext.Provider>
  );
};

/**
 * useSidebar Hook
 *
 * Purpose: Access sidebar state from any component
 * Usage: const { sidebarOpen, toggleSidebar } = useSidebar();
 */
export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export default SidebarContext;
